import {T} from 'ramda';
import {of, filter, first, fromEvent, map, NEVER, skip, tap} from 'rxjs';
import Decimal, {DecimalSource} from './break_infinity.js';
import * as D from './decimal_utils';
import {ri} from './runner-idle';
import {Stat} from './stat';
import {StatName} from './types/runner-idle.js';
import {capitalizeFirstLetter} from './utils.js';

export const statNames = [
  'null',
  // Body stats
  'strength',
  'speed',
  'constitution',
  'endurance',
  'vitality',
  // Mind stats
  'perception',
  'intelligence',
  'wisdom',
  // Skill stats
  'fishing',
  // Magic stats
  'magic',
  'lightMagic',
  'fireMagic',
  'crystalMagic',
  'timeMagic',
  'spaceMagic',
  'witherMagic',
] as const;

export function initializeStats() {
  function initStat(name: StatName, overrides: Partial<Stat>) {
    ri.stats[name].setup({
      name,
      displayName: capitalizeFirstLetter(name),
      shortName: name.slice(0, 3).toUpperCase(),
      minimum: overrides.minimum ?? new Decimal(1),
      // unlock$: ri.stats[name].baseValue$.pipe(
      //   map((val) => val.gt(ri.stats[name].minimum))
      // ),
      // visible$: ri.stats[name].unlock$,
      ...overrides,
    });
    ri.stats[name].initializeValue(overrides.value ?? 1);
    ri.stats[name].requiredStats$ =
      overrides.requiredStats$ ??
      of({
        [name]: 'anyEver',
      });
    ri.stats[name].unlock$ = overrides.unlock$ ?? of(true);
    ri.stats[name].visible$ = overrides.visible$ ?? of(false);
  }

  initStat('null', {
    shortName: '',
    description: '',
    unlock$: of(false),
  });

  initStat('speed', {
    shortName: 'SPD',
    description:
      "Increases <span class='fancy distance'>Distance</span> traveled per <strong>second</strong>, but doesn't change distance per <strong>stamina</strong>.",
  });

  initStat('strength', {
    description: 'Reduces Stamina cost when Traveling (Walk, Jog, etc.).',
  });

  initStat('constitution', {
    description: 'Improves how much Stamina you get from Resting',
  });

  initStat('endurance', {
    description: 'Increases Stamina capacity.',
  });

  initStat('vitality', {
    description: 'Increases Health capacity.',
  });

  initStat('wisdom', {
    description: "This isn't D&D. You're a wizard, be wise!<br>Increases Mana",
    // persistentUnlock: true,
    persistentValue: true,
  });

  initStat('perception', {
    description:
      "See more better. Find secrets to improve.<br>Improves Hunting and Gathering accuracy.<br>Increases your Map's max zoom level.",
    conditionString: 'Click a secret!',
    visible$: of(true),
    minimum: new Decimal(-9),
    // persistentUnlock: true,
    // persistentValue: true,
  });

  initStat('fishing', {
    description: 'Catch fish.',
  });

  initStat('intelligence', {
    description:
      'Gain by Reading and casting Spells.<br>Unlocks Spells and various tools.',
  });

  function createMagicStat(overrides: Partial<Stat>) {
    if (!overrides.name) throw new Error('Magic stat must have a name');
    initStat(overrides.name, {
      minimum: new Decimal(0),
      value: new Decimal(0),
      requiredStats$: of({
        [overrides.name]: 'anyEver',
      }),
      // condition: () => stat.value.gt(0),
      // progress: () => new Decimal(0),
      // visibleCondition: () =>
      //   Object.values(ri.spells)
      //     .filter((spell) => spell.tags.has(stat.name))
      //     .some((spell) => spell.unlocked),
      ...overrides,
    });
    ri.stats[overrides.name].baseValue$.next(new Decimal(overrides.value ?? 0));
  }

  createMagicStat({
    name: 'magic',
    description: 'Manipulate the world around you.',
  });

  createMagicStat({
    name: 'lightMagic',
    displayName: 'Light Magic',
    shortName: 'LGT',
    description: 'Manipulate light.',
  });

  createMagicStat({
    name: 'crystalMagic',
    displayName: 'Crystal Magic',
    description: 'Manipulate crystalline materials.',
  });

  createMagicStat({
    name: 'fireMagic',
    displayName: 'Fire Magic',
    description: 'Manipulate fire.',
  });

  createMagicStat({
    name: 'timeMagic',
    displayName: 'Time Magic',
    description: 'Manipulate time.',
  });

  createMagicStat({
    name: 'spaceMagic',
    displayName: 'Space Magic',
    shortName: 'SPC',
    description: 'Manipulate space. Passively increases storage.',
  });

  createMagicStat({
    name: 'witherMagic',
    displayName: 'Wither Magic',
    shortName: 'WTH',
    description:
      'An uncertain path to power, draining yourself to feed dark magics.',
  });
}
